import type { Command } from '@fabricos/protocol-ts';
import { sendCommand } from '@/hooks/useHubSocket';
import { log } from '@/lib/logger';
import { useGotoTargets } from '@/store/gotoTargets';

/**
 * Cancel an in-flight GOTO for a single asset.
 *
 * Sends a `set_mode "brake"` command (ArduCopter BRAKE; PX4 maps it to
 * AUTO_LOITER) AND clears the on-map target marker locally. BRAKE, not
 * LOITER: LOITER obeys the RC throttle, and with no RC attached (GCS-only
 * ops, SITL) that reads as throttle-low → the drone DESCENDS instead of
 * holding (operator hit this 2026-07-14; same reasoning as the manual
 * watchdog's ADR "brake-not-loiter"). The mode switch is the load-bearing
 * part: just clearing the marker would leave it flying to the original
 * GOTO while the operator believes it cancelled.
 *
 * Returns whether the command was queued; the marker clear happens regardless
 * (we'd rather the UI reflect the operator's intent than wait for an ack).
 */
export function cancelGoto(robotId: string): boolean {
  const cmd: Command = {
    kind: 'set_mode',
    id: makeId(),
    robot_id: robotId,
    priority: 'normal',
    mode: 'brake',
  };
  const ok = sendCommand(cmd);
  useGotoTargets.getState().clear(robotId);
  log.action('GOTO cancelled', { robotId, queued: ok });
  return ok;
}

/**
 * Cancel GOTO for every asset that currently has a target. Useful as a
 * "panic stop" for swarm operations.
 */
export function cancelAllGotos(): number {
  const targets = useGotoTargets.getState().targets;
  const ids = Object.keys(targets);
  for (const id of ids) cancelGoto(id);
  return ids.length;
}

function makeId(): string {
  return `cmd-${Math.random().toString(36).slice(2, 10)}-${Date.now()}`;
}
